import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

export function MosaicLogo({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" aria-hidden="true">
      <rect x="2" y="2" width="18" height="18" rx="3.5" fill="#52121E" />
      <rect x="22.5" y="2.5" width="17" height="17" rx="3.2" fill="none" stroke="#52121E" strokeWidth="1.5" opacity="0.55" />
      <rect x="2.5" y="22.5" width="17" height="17" rx="3.2" fill="none" stroke="#52121E" strokeWidth="1.5" opacity="0.55" />
      <rect x="22" y="22" width="18" height="18" rx="3.5" fill="#7A1C2C" />
    </svg>
  );
}

export function Wordmark({ size = 18 }: { size?: number }) {
  return (
    <span className="leading-none tracking-[-0.02em] text-ink" style={{ fontSize: size, fontWeight: 600 }}>
      Lex<span className="font-semibold text-secondary">Mosaic</span>
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle: string; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div className="mono mb-2 text-[11px] uppercase tracking-[0.16em] text-muted">Workspace</div>
        <h1 className="text-[34px] font-semibold leading-none tracking-[-0.02em] text-ink md:text-[38px]">{title}</h1>
        <p className="mt-3 max-w-2xl text-[14.5px] leading-6 text-secondary">{subtitle}</p>
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function Button({ children, variant = "primary", className = "", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode; variant?: "primary" | "secondary" | "quiet" }) {
  const classes =
    variant === "primary"
      ? "bg-primary text-white shadow-[0_4px_12px_rgba(122,28,44,0.22)] hover:brightness-95"
      : variant === "secondary"
        ? "border border-line bg-surface text-ink hover:bg-hover"
        : "text-secondary hover:bg-hover";
  return (
    <button {...props} className={`min-h-11 rounded-lg px-4 text-sm font-semibold ${classes} ${className}`}>
      {children}
    </button>
  );
}

export function Section({ title, children, aside, kicker }: { title: string; children: ReactNode; aside?: ReactNode; kicker?: string }) {
  return (
    <section className="mb-6">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          {kicker ? <div className="mono mb-1 text-[10.5px] uppercase tracking-[0.14em] text-muted">{kicker}</div> : null}
          <h2 className="text-base font-semibold tracking-[-0.01em] text-ink">{title}</h2>
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Card({ children, className = "", ...props }: HTMLAttributes<HTMLDivElement> & { children: ReactNode; className?: string }) {
  return (
    <div {...props} className={`rounded-xl border border-line bg-surface p-4 shadow-[0_1px_0_rgba(11,31,58,0.02)] ${className}`}>
      {children}
    </div>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return <span className="mono text-[10.5px] font-medium uppercase tracking-[0.13em] text-muted">{children}</span>;
}

export function Avatar({ name, size = "md", imageUrl, shape = "circle" }: { name: string; size?: "sm" | "md" | "lg"; imageUrl?: string | null; shape?: "circle" | "rounded" }) {
  const initials = name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2);
  const sizeClass = size === "sm" ? "h-8 w-8 text-xs" : size === "lg" ? "h-14 w-14 text-base" : "h-10 w-10 text-sm";
  const shapeClass = shape === "rounded" ? "rounded-lg" : "rounded-full";
  if (imageUrl) {
    return <img src={imageUrl} alt="" className={`shrink-0 border border-line bg-[#E5E8EE] object-cover ${sizeClass} ${shapeClass}`} />;
  }
  return <span className={`grid shrink-0 place-items-center border border-line bg-[#E5E8EE] font-semibold tracking-[0.01em] text-ink ${sizeClass} ${shapeClass}`}>{initials}</span>;
}

export function Tag({ children }: { children: ReactNode }) {
  return <span className={`mono inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-medium ${tagColor(children)}`}>{children}</span>;
}

const tagPalettes = [
  "border-sky-200 bg-sky-50 text-sky-800",
  "border-emerald-200 bg-emerald-50 text-emerald-800",
  "border-violet-200 bg-violet-50 text-violet-800",
  "border-amber-200 bg-amber-50 text-amber-800",
  "border-rose-200 bg-rose-50 text-rose-800",
  "border-cyan-200 bg-cyan-50 text-cyan-800",
  "border-indigo-200 bg-indigo-50 text-indigo-800",
  "border-lime-200 bg-lime-50 text-lime-800",
];

function tagColor(children: ReactNode) {
  const label = typeof children === "string" || typeof children === "number" ? String(children) : "";
  const normalized = label.toLowerCase();
  if (/\b(adopted|accepted|active|approved|open|support|cleared)\b/.test(normalized)) return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (/\b(rejected|declined|archived|closed|oppose|conflict|inactive)\b/.test(normalized)) return "border-rose-200 bg-rose-50 text-rose-800";
  if (/\b(vote|voting|deadline|due|urgent|amendment|review)\b/.test(normalized)) return "border-amber-200 bg-amber-50 text-amber-800";
  if (/\b(data|privacy|gdpr|cyber|digital|dora|ai)\b/.test(normalized)) return "border-sky-200 bg-sky-50 text-sky-800";
  if (/\b(eu|france|germany|italy|spain|luxembourg|belgium|netherlands|global|cross-border)\b/.test(normalized)) return "border-indigo-200 bg-indigo-50 text-indigo-800";
  if (/\b(proposal|project|governance|committee|members)\b/.test(normalized)) return "border-violet-200 bg-violet-50 text-violet-800";
  if (!label) return "border-line bg-surface text-secondary";
  let hash = 0;
  for (let index = 0; index < label.length; index += 1) hash = (hash * 31 + label.charCodeAt(index)) % tagPalettes.length;
  return tagPalettes[hash];
}

export function EmptyState({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-line bg-surface p-5 text-sm text-secondary">
      <div>{children}</div>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

// Status pill — single source of truth for tone colours (uses the semantic tokens).
export type PillTone = "good" | "warn" | "bad" | "info" | "neutral";
const pillTones: Record<PillTone, string> = {
  good: "border-goodBorder bg-goodBg text-good",
  warn: "border-warnBorder bg-warnBg text-warn",
  bad: "border-badBorder bg-badBg text-bad",
  info: "border-infoBorder bg-infoBg text-info",
  neutral: "border-line bg-surfaceAlt text-secondary",
};
const pillDots: Record<PillTone, string> = {
  good: "bg-good",
  warn: "bg-warn",
  bad: "bg-bad",
  info: "bg-info",
  neutral: "bg-muted",
};

export function Pill({ tone = "neutral", dot = false, className = "", children }: { tone?: PillTone; dot?: boolean; className?: string; children: ReactNode }) {
  return (
    <span className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold ${pillTones[tone]} ${className}`}>
      {dot ? <span className={`h-1.5 w-1.5 rounded-full ${pillDots[tone]}`} /> : null}
      {children}
    </span>
  );
}

// Country/jurisdiction square badge — shared so admin, expertise and legislative views match.
export function CountryBadge({ code, size = "sm" }: { code: string; size?: "sm" | "md" }) {
  const dim = size === "md" ? "h-9 w-9 rounded-lg text-[11px]" : "h-7 w-7 rounded-md text-[10px]";
  return <span className={`grid ${dim} shrink-0 place-items-center bg-ink font-mono font-semibold text-white`}>{code || "··"}</span>;
}
